import * as Sentry from '@sentry/vue'

function getSentryIntegrations() {
  if (!process.client) return []

  const router = useRouter()
  return [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.replayCanvasIntegration()
  ]
}

export default defineNuxtPlugin({
  name: 'sentry',
  parallel: true,
  setup(nuxtApp) {
    const vueApp = nuxtApp.vueApp
    const config = useRuntimeConfig()
    const userStore = useUserStore()
    Sentry.setUser({
      id: userStore.user?.id?.toString(),
      name: userStore.user?.name,
      email: userStore.user?.email
    })
    Sentry.init({
      app: vueApp,
      dsn: config.public.SENTRY_DSN_PUBLIC,
      integrations: getSentryIntegrations(),

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

      // This sets the sample rate. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 1,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1,

      // Filter out errors from node_modules
      beforeSend(event) {
        // Check if any stack frames are from node_modules
        if (event.exception) {
          const frames = event.exception.values?.[0]?.stacktrace?.frames
          if (frames) {
            const hasNodeModules = frames.some(
              (frame) =>
                frame.filename?.includes('node_modules') ||
                frame.filename?.includes('/node_modules/')
            )
            if (hasNodeModules) {
              return null // Don't send this event to Sentry
            }
          }
        }
        return event
      }
    })

    vueApp.config.errorHandler = (err, instance, info) => {
      Sentry.captureException(err, {
        contexts: {
          vue: {
            componentName: instance?.$options?.name || 'Unknown',
            lifecycleHook: info
          }
        }
      })
      // eslint-disable-next-line no-console
      console.error('Error captured by Sentry:', err)
    }
  }
})
